import { supabase } from './supabase';
import { sendNotification, getBranchManagerIds, getPurchasingIds, getInvoiceReconcilerIds } from './notify';
import { EVENT_AWAITING_ENTRY, INVOICING_DOMAIN } from './invoiceDiscrepancy';
import { notifyRequestReceived } from './materialRequestEmails';
import {
  resolveAutoPrintOption,
  dispatchZebraPrint,
  buildInventoryZpl,
  printInventoryLabel,
  optionFormatId,
  LABEL_PROFILES,
} from '../utils/printLabel';
import { DESTINATIONS, getFormat } from './labelFormats';

// Canonical copy shown on ReceiveSheetContent's print toggle. Kept here
// rather than in printLabel.js since it's receive-flow display text, not
// printer-resolution logic — printLabel.js's resolver stays copy-agnostic.
//
// #337, Session 77. This line was the most exposed instance of the defect
// Session 76 fixed in the batch modal: it read a LABEL_PROFILES row and told the
// receiver "PT-E560BT · 2\"×1\" · tap to skip" — naming a machine Stede has no
// way to identify, on the one toggle Dallas sees every time it receives stock in
// September. The mechanism is the browser's print dialog; the job goes wherever
// the OS is pointed.
//
// The Zebra branch still names its printer, and that is correct rather than
// inconsistent: a Zebra is a row in printer_registry with an IP address and a
// heartbeat, so Stede genuinely knows which machine it means.
//
// #334: the size comes from the format ladder, the same table the batch modal's
// chips read, instead of a profile's hand-written `desc` string.
export function describeReceivePrintOption(option) {
  if (!option) return 'No printer configured for this branch';
  if (option.type === 'zebra') {
    return `${option.printer?.name || 'Zebra printer'} \u00b7 tap to skip`;
  }
  const format = getFormat(option.formatId || optionFormatId(option));
  const size = format ? ` \u00b7 ${format.label}` : '';
  return `${DESTINATIONS.local.label}${size} \u00b7 tap to skip`;
}

// Resolves which printer this branch will auto-print to on receive, plus
// the copy to show on ReceiveSheetContent's toggle. Call once when a
// receive sheet opens; pass the returned printOption straight through to
// submitReceiveStock() at submit time rather than re-resolving, so what
// the user saw on the toggle is exactly what gets dispatched.
export async function resolveReceivePrintOption(branchName) {
  const option = await resolveAutoPrintOption(branchName, 'inventory');
  return { option, description: describeReceivePrintOption(option) };
}

// Looks up the price to freeze onto this transaction — Issue #171.
// Prefers the exact vendor this order was placed with (matchedRequest.vendor_id);
// falls back to the item's preferred vendor if no matched order/vendor exists.
// Returns null (never a guessed number) if neither lookup finds a price, matching
// this app's existing convention of showing an honest "—" over a fabricated cost.
async function getUnitCostSnapshot(itemId, vendorId) {
  if (vendorId) {
    const { data } = await supabase
      .from('vendor_items')
      .select('unit_cost')
      .eq('item_id', itemId)
      .eq('vendor_id', vendorId)
      .maybeSingle();
    if (data?.unit_cost != null) return data.unit_cost;
  }
  const { data: preferred } = await supabase
    .from('vendor_items')
    .select('unit_cost')
    .eq('item_id', itemId)
    .eq('is_preferred', true)
    .maybeSingle();
  return preferred?.unit_cost ?? null;
}

// #247, Session 78c. What one received unit is actually worth, for a receive that
// settles a purchase document.
//
// The receive path used to call getUnitCostSnapshot() directly and stamp the
// vendor's CURRENT LIST PRICE onto every transaction — a number nobody committed
// to, free to drift between order and delivery, and permanently different from
// the truth on a contract-locked item. Since Session 74k4 the real committed
// price has been sitting on the order line as price_per_pack, and the receive
// path has never read it.
//
// This is NetSuite's rule stated exactly: the receipt-time cost drives the
// average, and a vendor bill that later disagrees posts a variance rather than
// rewriting history. It is also the reason #343 exists — Stede has no variance
// step and no path from an invoice back to the cost basis at all, which is a
// larger gap than this function closes.
//
// **Why per-unit is price_per_pack / pack_qty and NOT ordered_price / qty_requested.**
// `ordered_price` is GENERATED ALWAYS AS (price_per_pack * packs_ordered) — a LINE
// TOTAL. Purchasing orders whole packs, so packs_ordered * pack_qty is routinely
// MORE than qty_requested: one $600 pallet of 50 ordered against a 30-sheet
// request would book $20/sheet instead of $12, overstating the ledger by two
// thirds on every rounded-up order. This mirrors PurchasingQueuePage.js's own
// unit-cost display, which is the number purchasing was looking at when they
// committed.
//
// **Falls back to the list price rather than to null**, deliberately: an
// unpriced receive must be no worse off than it was before #247, and a request
// line predating 74k4 carries no price anywhere. The whole regression surface is
// therefore lines that DO carry a committed price, which before today were being
// valued at list.
//
// **A known, accepted exposure, recorded rather than discovered later:** pack_qty
// is read live from vendor_items and is NOT frozen onto the request line at Mark
// Ordered time, so a vendor who changes their pack size between order and
// delivery shifts this number. price_per_pack IS frozen. Freezing pack_qty too
// needs a migration and belongs with #191's three-way match, not here. Pack size
// changes far less often than price, which is why this is an exposure rather than
// a defect — but it is the first thing to suspect if a received value ever looks
// wrong by a clean multiple.
export async function resolveReceiveUnitCost({ itemId, vendorId, requestItemId }) {
  if (requestItemId != null && itemId != null) {
    const { data: line } = await supabase
      .from('material_request_items')
      .select('price_per_pack')
      .eq('id', requestItemId)
      .maybeSingle();

    const perPack = line?.price_per_pack != null ? parseFloat(line.price_per_pack) : null;
    if (Number.isFinite(perPack)) {
      // Deliberately the same lookup #333 built, not a second one. A pack size
      // and a pack price are two halves of one fact, and reading them from
      // different rows is how they would come to disagree.
      const { packQty } = await fetchPackSnapshot(itemId, vendorId);
      const divisor = Number.isFinite(packQty) && packQty > 0 ? packQty : 1;
      return perPack / divisor;
    }
  }

  return getUnitCostSnapshot(itemId, vendorId);
}

// #333. How many PACKAGES a received quantity represents — the number of
// physical things that arrived and therefore the number of labels, as distinct
// from how much arrived. Fifty sheets of MDO on one pallet is one thing in one
// place; Stede printed fifty stickers for it because the receive quantity was
// passed straight through as the copy count.
//
// Rounds UP deliberately, and that is a decision rather than a default: 60
// sheets against a 50-sheet pallet is two labels, because a part-pallet is
// still a physical object sitting on the floor needing identification.
// Rounding down would leave it unlabelled — the exact failure #333 exists to
// prevent, reintroduced by arithmetic.
//
// packQty <= 1 returns the quantity unchanged, which is every item nobody has
// configured a pack size for. vendor_items.pack_qty is NOT NULL DEFAULT 1, so
// that is the overwhelming majority of the catalog today and its behaviour is
// bit-for-bit what it was before this function existed. That is the whole
// regression surface for #333, and it is empty.
export function packagesFor(qty, packQty) {
  const q = Number(qty);
  if (!Number.isFinite(q) || q <= 0) return 0;
  const p = Number(packQty);
  if (!Number.isFinite(p) || p <= 1) return Math.ceil(q);
  return Math.ceil(q / p);
}

// #333. The pack size governing this receive, read at sheet-open time so the
// label count can be shown and changed before anything is committed.
//
// Same preference order getUnitCostSnapshot() uses below — the order's own
// vendor first, the item's preferred vendor as fallback — because the pack size
// and the frozen price are two facts about the same vendor_items row and must
// not be able to come from different rows. Fishbowl's model, which is the one
// Stede already implements: purchase UOM is defined per vendor, not per item,
// so the same part can arrive in 25-ft rolls from one supplier and 150-ft rolls
// from another.
//
// Deliberately NOT merged with getUnitCostSnapshot() into one shared query.
// That function falls through to the preferred vendor when the matched row's
// unit_cost is NULL, which is right for a price — a row with no price should
// not veto a price that exists elsewhere. pack_qty is NOT NULL, so it can never
// be missing from a row that exists, and folding the two together would have
// changed the cost lookup's fall-through behaviour to get at a column that
// never needs it. #333 is not allowed to move a number on the ledger.
//
// Never returns null: a miss is packQty 1, which is exactly today's behaviour,
// so no caller has to branch on absence.
export async function fetchPackSnapshot(itemId, vendorId) {
  const EMPTY = { packQty: 1, packUnit: null, vendorName: null };
  if (itemId == null) return EMPTY;
  const columns = 'pack_qty, pack_unit, inventory_vendors ( name )';

  if (vendorId) {
    const { data } = await supabase
      .from('vendor_items')
      .select(columns)
      .eq('item_id', itemId)
      .eq('vendor_id', vendorId)
      .maybeSingle();
    if (data) return shapePackSnapshot(data);
  }
  const { data: preferred } = await supabase
    .from('vendor_items')
    .select(columns)
    .eq('item_id', itemId)
    .eq('is_preferred', true)
    .maybeSingle();
  return preferred ? shapePackSnapshot(preferred) : EMPTY;
}

function shapePackSnapshot(row) {
  const raw = parseInt(row?.pack_qty, 10);
  return {
    packQty: Number.isFinite(raw) && raw > 0 ? raw : 1,
    packUnit: row?.pack_unit || null,
    vendorName: row?.inventory_vendors?.name || null,
  };
}

// #333. The delivery sheet's version — one request, one vendor, many lines,
// each with its own pack size. Two queries for the whole sheet rather than two
// per line: the vendor's own rows in one call, then a single preferred-vendor
// call covering only the items that vendor does not carry.
//
// Returns a Map keyed by item_id. Items with no vendor row at all are simply
// absent, and callers fall back to packagesFor()'s packQty <= 1 behaviour.
export async function fetchPackSnapshots(itemIds, vendorId) {
  const ids = Array.from(new Set((itemIds || []).filter(id => id != null)));
  const out = new Map();
  if (ids.length === 0) return out;
  const columns = 'item_id, pack_qty, pack_unit, inventory_vendors ( name )';

  if (vendorId) {
    const { data } = await supabase
      .from('vendor_items')
      .select(columns)
      .eq('vendor_id', vendorId)
      .in('item_id', ids);
    (data || []).forEach(row => out.set(row.item_id, shapePackSnapshot(row)));
  }

  const missing = ids.filter(id => !out.has(id));
  if (missing.length > 0) {
    const { data } = await supabase
      .from('vendor_items')
      .select(columns)
      .eq('is_preferred', true)
      .in('item_id', missing);
    (data || []).forEach(row => {
      if (!out.has(row.item_id)) out.set(row.item_id, shapePackSnapshot(row));
    });
  }
  return out;
}

// Blends this receive's price into the branch's running average cost —
// standard moving-average costing, the same method QuickBooks/NetSuite
// default to and SAP's Standard/Moving Average setting uses. Existing
// on-hand value blends with the value of what just arrived, weighted by
// quantity. If this receive's price is unknown, the average is left
// unchanged rather than corrupted with a guess. If there's no valid
// existing quantity to blend against (first stock ever, or count is
// currently negative pending an audit), the average simply resets to
// this receive's price.
// EXPORTED at Session 79.6 for #371. Gate decision D16 says the transfer
// receive path reuses this rather than carrying a second copy of the formula,
// and a second copy is exactly how #357's four disagreeing getStatus() copies
// happened. It was module-private until now only because nothing outside this
// file had ever needed it.
//
// The two awkward cases it already handles are the two a transfer needs most:
// an unknown price leaves the receiver's average ALONE rather than zeroing it
// (which is why stake costs were deliberately left empty on staging at 79.5),
// and a first-ever receive sets it.
export function computeNewAverageCost(currentQty, currentAvg, receivedQty, receivedUnitCost) {
  if (receivedUnitCost == null) return currentAvg ?? null;
  if (currentQty <= 0 || currentAvg == null) return receivedUnitCost;
  const totalValue = (currentQty * currentAvg) + (receivedQty * receivedUnitCost);
  const totalQty = currentQty + receivedQty;
  return totalQty > 0 ? totalValue / totalQty : receivedUnitCost;
}

// #279. Finds the request line this receive is settling. Deliberately stricter
// than ReceiveSheetContent's resolveRequestLine(), which falls back to
// material_request_items[0] when nothing matches — correct for *displaying* a
// quantity, and wrong here. This value selects the row that gets incremented, so
// a fallback would credit the delivery to a line that did not arrive: the exact
// class of error #279 exists to fix, committed by the fix itself.
//
// Returns null when nothing matches, and the caller then records the stock
// movement without touching the request. Never guesses.
function findRequestLineForItem(matchedRequest, itemId, requestItemId = null) {
  const lines = matchedRequest?.material_request_items;
  if (!Array.isArray(lines)) return null;
  // #509, Session 79.21c -- AN OFF-CATALOG LINE HAS item_id NULL BY DESIGN, so
  // it cannot be found by item and can only be found by its own row id. The
  // caller passes that id; the catalog route below is untouched.
  //
  // Matching on `l.item_id === itemId` with both null would be far worse than
  // not matching at all: EVERY off-catalog line on the request would compare
  // equal, so a request holding two of them would settle whichever sorted
  // first. Hence the explicit id rather than a relaxed comparison.
  if (requestItemId != null) return lines.find(l => l.id === requestItemId) || null;
  if (itemId == null) return null;
  return lines.find(l => l.item_id === itemId) || null;
}

// What is still owed on a line — mirrors ReceiveSheetContent's outstandingFor().
// Passed to submitReceiveDelivery() so its over-receipt warning measures against
// the remainder rather than against the original order quantity, which would stop
// warning as soon as any part of the line had been received.
function outstandingOf(line) {
  if (!line) return null;
  const requested = parseFloat(line.qty_requested);
  if (!Number.isFinite(requested)) return null;
  const receivedRaw = line.qty_received != null ? parseFloat(line.qty_received) : 0;
  return requested - (Number.isFinite(receivedRaw) ? receivedRaw : 0);
}

// Canonical receive-stock implementation — Issue #13. Replaces three
// independently-diverged copies (InventoryItemDetail.js,
// InventoryActionCatalog.js, RequestsInboxPage.js's ReceiveSheetPortal).
//
// Two paths, and which one runs is decided by whether this receive settles a
// purchase document — amended at 74w for #279, when the request half moved out.
//
// MATCHED (a request line resolves): delegates entirely to
// submitReceiveDelivery() below, which posts through
// receive_material_request_items(). That covers the per-line qty_received, the
// close-only-when-complete test, the WO# override audit (Engineering Standards
// rule 19), the over-receipt warning, notifications and the email. Nothing about
// a request is written here any more, so the two implementations cannot drift —
// the reason #279 existed at all.
//
// UNMATCHED (a plain stock receipt, no request): everything below, unchanged.
//  - the inventory_transactions insert and inventory_stock update
//  - printing through the real per-branch resolver instead of a hardcoded
//    Brother bypass (Bypass #4)
//  - a clear, user-facing message on the job_ref_format constraint (#127)
//    instead of a raw Postgres error
//
// Returns { allComplete, printResult, ... } on the matched path — allComplete is
// the database's own all_complete, and the callers use it to say whether the
// order closed rather than asserting that it did.
export async function submitReceiveStock({
  item,            // { id, name, sku, uom } - id is NULL on an off-catalog line
  // #509, Session 79.21c. An off-catalog line is identified by its own request
  // row rather than by a catalog item, because it has none.
  requestItemId = null,
  isOffCatalog = false,
  branchId,
  branchName,
  qty,
  jobRef,
  notes,
  packingSlip,     // File — image or PDF
  matchedRequest,  // full material_requests row, or null
  printLabels,
  printOption,     // resolved via resolveReceivePrintOption(), or null
  labelCopies,     // #333 — packages, from the sheet's stepper. Omit for qty.
  profile,         // { id, full_name }
}) {
  // #279. A receive against a real request is a goods receipt against a purchase
  // document, and it goes through the one implementation that treats it as such.
  // receive_material_request_items() adds to this line's qty_received additively
  // and closes the header only when every line is satisfied; the code that used to
  // run here set status 'received' unconditionally and wrote qty_received nowhere,
  // so receiving one line of a two-line order closed the whole thing and left no
  // record of what actually arrived.
  //
  // This is the migration Session 74k7 did not do: it added the RPC for the
  // multi-item delivery sheet only, leaving the single-item path on the old write.
  // Both implementations then lived in this file, one maintaining the state the
  // other corrupted. Locked rule 7 wants this write in a Postgres function anyway.
  //
  // A receive with no matched request falls through to the path below and is
  // unchanged — a miscellaneous stock receipt is not a goods receipt and has no
  // purchase document to settle.
  const requestLine = findRequestLineForItem(matchedRequest, item.id, requestItemId);
  // #509 -- AN OFF-CATALOG RECEIVE HAS NOWHERE ELSE TO GO, AND FAILING LOUDLY IS
  // THE POINT. The unmatched branch below is a plain stock receipt: it writes an
  // inventory_transactions row against item.id. For an off-catalog line that id
  // is null, so falling through would either throw deep in the database or write
  // a transaction against nothing -- and this project's recurring defect is the
  // silent version of exactly that. If the line cannot be found, say so here.
  if (isOffCatalog && !requestLine) {
    throw new Error(
      'This off-catalog line could not be matched to its order, so nothing was '
      + 'recorded. Reopen the order and try again.'
    );
  }
  if (matchedRequest && requestLine) {
    const result = await submitReceiveDelivery({
      request: matchedRequest,
      lines: [{
        request_item_id: requestLine.id,
        item_id: item.id,
        qty,
        // #509 -- submitReceiveDelivery already handles an off-catalog line
        // correctly: it resolves no unit cost and posts NO inventory
        // transaction, because there is no stock record for the cost to attach
        // to. That is SAP's consumption answer and it was already written; this
        // path simply stopped hard-coding false.
        is_off_catalog: !!isOffCatalog,
        name: item.name,
        sku: item.sku,
        uom: item.uom,
        outstanding: outstandingOf(requestLine),
        // #333. The matched path delegates entirely to submitReceiveDelivery,
        // so the receiver's label count has to travel on the line rather than
        // as a sibling argument — otherwise the one receive that settles a
        // purchase document would silently ignore the stepper the receiver
        // just used, which is the half of the flow Dallas will use most.
        labelCopies,
      }],
      branchId,
      branchName,
      jobRef,
      notes,
      packingSlip,
      printLabels,
      printOption,
      profile,
    });
    return {
      ...result,
      allComplete: result?.all_complete !== false,
      printResult: (result?.printResults || [])[0] ?? null,
    };
  }

  // #497, Session 79.1 — THE PHOTO IS EVIDENCE; THE COUNT IS THE FACT.
  //
  // What this used to do, and what it cost. The upload ran first and THREW, so a
  // photo that failed to reach storage meant nothing at all was recorded — no
  // stock movement, no transaction row, no history. The receiver saw "Packing slip
  // upload failed", which any reasonable person reads as *the count went in and
  // only the photo did not*. It had not. Observed live at Session 79 running the
  // field pack's line F2: quantity 10 with a real image attached, on-hand 9 before
  // and 9 after. Losing the fact because the evidence failed is backwards, and it
  // is worst exactly where it happens most — a phone on shop wifi at a loading
  // dock, with a signal that comes and goes.
  //
  // THE CORRECT BEHAVIOUR ALREADY EXISTED ONE PATH OVER, which is what settled the
  // design rather than any argument here: the off-catalog REQUEST path in
  // RequestMaterialsPage.js hits the same storage and completes normally, keeping
  // the work and dropping the photo. This now matches it. The one thing the
  // request path did wrong — never SAYING the photo was lost — is fixed there too.
  //
  // The failure is returned rather than swallowed. A silent degrade would trade
  // one wrong belief for another: the receiver would think a photo is on the
  // record when none is. Callers surface `photoFailed` on the success screen.
  let packing_slip_url = null;
  let photoFailed = false;
  if (packingSlip) {
    const ext = packingSlip.name.split('.').pop();
    const path = `packing-slips/${item.id}-${Date.now()}.${ext}`;
    const { error: upErr } = await supabase.storage
      .from('inventory-assets')
      .upload(path, packingSlip, { upsert: true });
    if (upErr) {
      photoFailed = true;
      console.error('#497 packing slip upload failed, receive continuing without it', upErr);
    } else {
      const { data: urlData } = supabase.storage.from('inventory-assets').getPublicUrl(path);
      packing_slip_url = urlData.publicUrl;
    }
  }

  // #238: a receive against an item with no stock row at this branch wrote
  // history and never created the row, because trg_update_stock's UPDATE
  // matched zero rows and the app only wrote back when stockData existed.
  // Seed the row at zero first; the trigger then increments it normally.
  await supabase
    .from('inventory_stock')
    .upsert(
      { item_id: item.id, branch_id: branchId, qty_on_hand: 0 },
      { onConflict: 'item_id,branch_id', ignoreDuplicates: true }
    );

  const { data: stockData } = await supabase
    .from('inventory_stock')
    .select('id, qty_on_hand, avg_unit_cost')
    .eq('item_id', item.id)
    .eq('branch_id', branchId)
    .single();

  const currentQty = stockData?.qty_on_hand ?? 0;
  const newQty = currentQty + qty;

  const unitCostSnapshot = await getUnitCostSnapshot(item.id, matchedRequest?.vendor_id || null);
  const newAvgCost = computeNewAverageCost(currentQty, stockData?.avg_unit_cost, qty, unitCostSnapshot);

  const { error: txErr } = await supabase
    .from('inventory_transactions')
    .insert({
      item_id: item.id,
      branch_id: branchId,
      user_id: profile.id,
      tx_type: 'receive',
      qty,
      qty_after: newQty,
      work_order_ref: jobRef || null,
      work_order_ref_note: notes || null,
      packing_slip_url,
      unit_cost_at_transaction: unitCostSnapshot,
    });

  if (txErr) {
    if (txErr.code === '23514' && txErr.message?.includes('job_ref_format')) {
      throw new Error(
        `"${jobRef}" isn't a valid work order number. Use 6 digits, optionally followed by -# for a sub-job (e.g. 123456 or 123456-1).`
      );
    }
    // #277: job_ref_required is being dropped, but a database that has not had
    // 20260819_drop_job_ref_required.sql applied yet still rejects a receive
    // carrying none of WO#, notes or a packing slip. Named rather than left to
    // the generic message below, because the generic one sent the receiver to a
    // dead end in front of three fields all labelled optional. This branch
    // becomes unreachable once the migration reaches production; it is here so
    // the confirm prompt can ship without waiting on it.
    if (txErr.code === '23514' && txErr.message?.includes('job_ref_required')) {
      throw new Error(
        'This receive needs a WO#, a note, or a packing slip photo before it can be saved. Add any one of the three and confirm again.'
      );
    }
    throw new Error('Failed to save the receive. Please try again.');
  }

  // #240: trg_update_stock already increments qty_on_hand atomically on
  // insert. Writing qty_on_hand from here too overwrote that increment
  // with a value read before the insert, so two concurrent receives of
  // the same item silently lost one. The database owns quantity; the
  // application owns cost only.
  if (stockData) {
    await supabase.from('inventory_stock').update({ avg_unit_cost: newAvgCost }).eq('id', stockData.id);
  }

  // #279. The request-closing block that used to sit here is gone. Every receive
  // that can be attributed to a request line now returns above, through
  // receive_material_request_items(). Reaching this point with a matchedRequest
  // set means the request carried no line for this item - its
  // material_request_items came back empty, or held no row whose item_id matches
  // the item being received.
  //
  // In that state the request is deliberately left untouched: not closed, not
  // stamped, not notified. The old code closed it regardless, which is the same
  // "declare it settled without knowing what settled it" move #279 is about. The
  // stock movement is still recorded and auditable; what is refused is the claim
  // that a specific purchase document was fulfilled by it.

  // #333. Copies are packages, not units. labelCopies is what the receiver saw
  // on the sheet's stepper; falling back to qty keeps every caller that has not
  // been given the control — and every test written before it existed —
  // printing exactly what it printed before.
  const copies = labelCopies != null ? labelCopies : qty;

  let printResult = null;
  if (printLabels && printOption && copies > 0) {
    if (printOption.type === 'zebra') {
      const zpl = buildInventoryZpl(item, branchName, LABEL_PROFILES[printOption.profileId]);
      printResult = await dispatchZebraPrint({
        printer: printOption.printer,
        userId: profile.id,
        itemType: 'inventory',
        itemId: item.id,
        copies,
        zplPayload: zpl,
      });
    } else if (printOption.type === 'local') {
      printResult = await printInventoryLabel(item, branchName, copies);
    }
  }

  // #497 — `photoFailed` travels with the result so the sheet can tell the truth:
  // the count is saved and the photo is not. Callers that ignore it behave exactly
  // as before.
  return { newQty, printResult, photoFailed };
}

// Canonical multi-item delivery receipt — Issue #212 Screen 8, Session 74k7.
// Distinct from submitReceiveStock() above, which stays in service for the three
// ad-hoc stock-receipt screens. A goods receipt against a purchase document and a
// miscellaneous stock receipt are different transaction types; this one knows a
// request, knows which of its lines arrived, and posts them as one atomic unit.
//
// Everything that touches the database happens inside a single Postgres function,
// receive_material_request_items(), so a delivery either records completely or not
// at all. Notifications and emails stay here in JavaScript and fire ONCE per
// delivery, never once per item — a delivery is one event, and five items must not
// produce five manager pings.
//
// Unit cost is resolved here rather than in SQL deliberately: getUnitCostSnapshot()
// already implements the vendor-preference lookup correctly, and reimplementing it
// in the function body would create a second copy free to drift from this one.
export async function submitReceiveDelivery({
  request,         // full material_requests row incl. material_request_items
  lines,           // [{ request_item_id, item_id, qty, is_off_catalog, name, uom, outstanding, labelCopies? }]
  branchId,
  branchName,
  jobRef,
  notes,
  packingSlip,     // File — image or PDF. One slip per delivery, not per item.
  printLabels,
  printOption,     // resolved via resolveReceivePrintOption(), or null
  profile,         // { id, full_name }
}) {
  if (!lines?.length) throw new Error('Nothing was checked off for this delivery.');

  // #497 — the second of the two upload sites, and it must move with the first.
  // This is the goods-receipt path: a delivery against a real purchase document,
  // where throwing on a failed photo discards not just a count but the settlement
  // of an order. Fixing one site and leaving the other is how the remaining
  // instance gets found by somebody at Dallas instead of here.
  let packing_slip_url = null;
  let photoFailed = false;
  if (packingSlip) {
    const ext = packingSlip.name.split('.').pop();
    const path = `packing-slips/req-${request.id}-${Date.now()}.${ext}`;
    const { error: upErr } = await supabase.storage
      .from('inventory-assets')
      .upload(path, packingSlip, { upsert: true });
    if (upErr) {
      photoFailed = true;
      console.error('#497 packing slip upload failed, delivery continuing without it', upErr);
    } else {
      const { data: urlData } = supabase.storage.from('inventory-assets').getPublicUrl(path);
      packing_slip_url = urlData.publicUrl;
    }
  }

  // Off-catalog lines have no catalog item and therefore no vendor price to
  // freeze. They post no inventory transaction at all, so there is nothing for a
  // cost to attach to.
  // #247. Was getUnitCostSnapshot(), which stamped the vendor's CURRENT LIST
  // PRICE onto every line. It now prefers what purchasing actually committed to
  // on this order's own line, and falls back to the list price only when the line
  // carries no committed price — so nothing is valued worse than it was before.
  //
  // This is the ONLY call site that changed, and that is by construction rather
  // than by luck: submitReceiveStock()'s matched branch delegates its whole
  // receive to this function with request_item_id already on the line, so the
  // single-item goods-receipt path inherits the fix without a second edit. Its
  // UNMATCHED branch — a plain stock receipt with no purchase document — has no
  // request line by definition and deliberately still resolves to list price.
  const priced = [];
  for (const l of lines) {
    const unit_cost = l.item_id
      ? await resolveReceiveUnitCost({
          itemId: l.item_id,
          vendorId: request.vendor_id || null,
          requestItemId: l.request_item_id,
        })
      : null;
    priced.push({ ...l, unit_cost });
  }

  const { data: result, error: rpcErr } = await supabase.rpc('receive_material_request_items', {
    p_request_id: request.id,
    p_branch_id: branchId,
    p_user_id: profile.id,
    p_items: priced.map(l => ({
      request_item_id: l.request_item_id,
      item_id: l.item_id ?? null,
      qty: l.qty,
      unit_cost: l.unit_cost,
    })),
    p_work_order_ref: jobRef || null,
    p_notes: notes || null,
    p_packing_slip_url: packing_slip_url,
  });

  if (rpcErr) {
    if (rpcErr.message?.includes('job_ref_format')) {
      throw new Error(
        `"${jobRef}" isn't a valid work order number. Use 6 digits, optionally followed by -# for a sub-job (e.g. 123456 or 123456-1).`
      );
    }
    throw new Error('Failed to save the delivery. Please try again.');
  }

  const managerIds = await getBranchManagerIds(branchId);
  const summary = lines
    .map(l => `${l.name} +${l.qty}${l.uom ? ' ' + l.uom : ''}`)
    .join(', ');
  const stillOpen = result?.all_complete === false;

  await sendNotification(
    managerIds,
    stillOpen ? 'Delivery received \u00b7 partial' : 'Delivery received',
    `${request.request_number} at ${branchName}: ${summary}.${stillOpen ? ' Some quantities are still outstanding.' : ''}`,
    'restock', 'inventory', 'requests_inbox', request.id
  );

  if (request.requested_by) {
    await sendNotification(
      [request.requested_by],
      stillOpen ? 'Part of your request has arrived' : 'Your request has arrived',
      `${request.request_number} at ${branchName}: ${summary}.${stillOpen ? ' The rest is still on order.' : ''}`,
      'restock', 'inventory', 'requests_inbox', request.id
    );
  }

  // ── #301, Session 79.12 — THE THREE-WAY MATCH IS NOW POSSIBLE ─────────────
  //
  // Decision 6 at 74y's gate was TWO invoicing events. The second — a
  // discrepancy being resolved — shipped at 74z4. This is the first, and it
  // has been RESERVED BY THE CHECK CONSTRAINT SINCE AUG 21 WITH NOTHING
  // WRITING IT: `invoice_awaiting_entry`, declared in invoiceDiscrepancy.js
  // and never called. NOTIFICATION_VOCABULARY.md recorded that accurately the
  // whole time — "Reserved by the constraint, not fired by anything."
  //
  // ** WHY HERE AND NOT AT ORDER TIME. ** Invoicing lists a request from the
  // moment it is `ordered`, so it is tempting to announce it then. That is the
  // wrong moment for the person doing the work: accounts payable cannot match
  // anything until the goods are in. A three-way match needs the order, the
  // RECEIPT and the invoice, and this is where the receipt arrives. SAP blocks
  // an invoice with no goods receipt for exactly this reason.
  //
  // ** AND ONLY WHEN THE ORDER IS COMPLETE. ** `all_complete` comes from the
  // database function, which decides it under a row lock — the same value
  // `stillOpen` above reads, rather than a second comparison that could
  // disagree with it. A partial delivery leaves the order unmatched, so
  // announcing one would be the #500 shape: a bell that fires on a day
  // nothing can be done is a bell people stop opening.
  //
  // ONE CALL SITE, NOT TWO. The vocabulary predicted both receive entry
  // points would need this. They do not: submitReceiveStock's matched path
  // DELEGATES ENTIRELY to this function, so the one place a purchase document
  // is settled is the one place this fires.
  if (!stillOpen) {
    const reconcilerIds = await getInvoiceReconcilerIds();
    if (reconcilerIds.length > 0) {
      await sendNotification(
        reconcilerIds,
        'Ready to invoice',
        `${request.request_number} at ${branchName} is fully received: ${summary}. The vendor invoice can be matched against it.`,
        EVENT_AWAITING_ENTRY, INVOICING_DOMAIN, 'purchasing_invoicing', request.id
      );
    }
  }

  // WO# override audit — Engineering Standards rule 19, never a silent overwrite.
  // The override decision is made by the database function, which compares against
  // the value it read under a row lock, so this reads its result rather than
  // recomputing the comparison against a value that may have moved.
  if (result?.wo_overridden) {
    await sendNotification(
      managerIds,
      `WO# corrected \u00b7 ${branchName}`,
      `${request.request_number}: WO# changed from "${result.prior_work_order_ref || '(none)'}" to "${jobRef}" while receiving. Changed by ${profile.full_name}.`,
      'audit', 'inventory', 'requests_inbox', request.id
    );
  }

  // Over-receipt warns and saves, never blocks — vendors overship, and blocking
  // would strand a receiver with no way to record physical reality. The receiving
  // user is already recorded regardless (inventory_transactions.user_id is NOT
  // NULL); this notification exists so the overage is noticed, not so it is
  // recorded. Reuses the existing 'audit' event type rather than inventing a new
  // one — confirmed against NOTIFICATION_VOCABULARY.md before writing the call
  // site, per the standing rule.
  const overLines = lines.filter(l => l.outstanding != null && l.qty > l.outstanding);
  if (overLines.length > 0) {
    const detail = overLines
      .map(l => `${l.name}: ordered ${l.outstanding}, received ${l.qty}`)
      .join('; ')
    await sendNotification(
      managerIds,
      `Over-receipt \u00b7 ${branchName}`,
      `${request.request_number} received above ordered quantity. ${detail}. Received by ${profile.full_name}.`,
      'audit', 'inventory', 'requests_inbox', request.id
    );
  }

  // Off-catalog arrival — Issue #241. The line records its received quantity on
  // the request and deliberately posts nothing to inventory, matching how SAP,
  // Oracle and Coupa receive a free-text line against the purchase document rather
  // than against an item master. Managers and purchasing are told because a
  // catalog decision is now outstanding; the receiver is not blocked by it.
  const offCatalogLines = lines.filter(l => !l.item_id);
  if (offCatalogLines.length > 0) {
    const purchasingIds = await getPurchasingIds();
    const audience = Array.from(new Set([...managerIds, ...purchasingIds]));
    const names = offCatalogLines.map(l => `${l.name} (${l.qty}${l.uom ? ' ' + l.uom : ''})`).join(', ');
    await sendNotification(
      audience,
      `Off-catalog item received \u00b7 ${branchName}`,
      `${request.request_number}: ${names} arrived and is not in the catalog. Stock was not updated for it \u2014 a catalog decision is pending.`,
      'audit', 'inventory', 'requests_inbox', request.id
    );
  }

  // One email per delivery, to branch managers only — the requester already has
  // the in-app notification above, an explicit Session 55 decision. The item list
  // goes into the template's Item row as one line per item; qty and uom are passed
  // as empty strings, not null, so detailRow()'s falsy check drops that row
  // instead of rendering the literal word "null" through its string interpolation.
  const emailItems = lines
    .map(l => `${l.name} \u2014 ${l.qty}${l.uom ? ' ' + l.uom : ''}`)
    .join('<br>');

  await notifyRequestReceived({
    branchId,
    branchName,
    requestNumber: request.request_number,
    itemName: emailItems,
    qtyReceived: '',
    uom: '',
    receivedByName: profile.full_name,
    // #126 — same id the paired sendNotification() above writes to link_id.
    requestId: request.id,
  });

  // Labels print per catalog item. #333: at that item's PACKAGE count, not its
  // received quantity — l.labelCopies is what the sheet showed the receiver for
  // that line, derived from the vendor's pack size and overridable there. A line
  // that carries no labelCopies falls back to its quantity, which is the
  // pre-#333 behaviour and is also what every pack_qty of 1 computes to anyway.
  //
  // Off-catalog lines are skipped — no catalog record, no SKU, nothing to encode
  // on a label.
  const printResults = [];
  if (printLabels && printOption) {
    for (const l of lines) {
      if (!l.item_id) continue;
      const copies = l.labelCopies != null ? l.labelCopies : l.qty;
      if (!(copies > 0)) continue;
      // #279. l.sku is preferred over the nested lookup because the single-item
      // callers now route through here and already hold the item's sku, while the
      // request rows they pass carry no nested inventory_items to look it up from —
      // both receive queries select material_request_items(id, item_id,
      // qty_requested, qty_received) only. Without this the label would print with
      // a blank SKU on every single-item receive. The delivery sheet passes no sku
      // and keeps using the lookup, so nothing changes for it.
      const src = (request.material_request_items || []).find(i => i.id === l.request_item_id);
      const item = {
        id: l.item_id,
        name: l.name,
        sku: l.sku ?? src?.inventory_items?.sku,
        uom: l.uom,
      };
      if (printOption.type === 'zebra') {
        const zpl = buildInventoryZpl(item, branchName, LABEL_PROFILES[printOption.profileId]);
        printResults.push(await dispatchZebraPrint({
          printer: printOption.printer,
          userId: profile.id,
          itemType: 'inventory',
          itemId: l.item_id,
          copies,
          zplPayload: zpl,
        }));
      } else if (printOption.type === 'local') {
        printResults.push(await printInventoryLabel(item, branchName, copies));
      }
    }
  }

  // #497 — see submitReceiveStock(). `result` is the RPC's own payload and carries
  // no such key, so this cannot collide with it.
  return { ...result, printResults, photoFailed };
}

// Provisional vendor_items row created when an off-catalog item was
// ordered — the same lookup ReceiveSheetPortal.handlePromoteToCatalog()
// already used, now shared so "Add to catalog" works identically from
// any of the three receive screens. Issue #16, resolved Session 71:
// requestItemId ties the lookup to the specific material_request_items
// row that was marked ordered (via vendor_items.provisional_request_item_id,
// written at Mark Ordered time in PurchasingQueuePage.js), instead of
// blindly returning whichever provisional row was created most recently.
// Returns null if no requestItemId is passed or no matching row exists —
// callers should treat a null result as "no provisional record," not an
// error, and let the user enter pricing manually rather than guessing.
export async function fetchProvisionalVendorItem(requestItemId) {
  if (!requestItemId) return null;
  const { data, error } = await supabase
    .from('vendor_items')
    .select('id, vendor_id, vendor_price, pack_qty, pack_unit, vendor_item_number, off_catalog_description')
    .eq('is_provisional', true)
    .eq('provisional_request_item_id', requestItemId)
    .order('created_at', { ascending: false })
    .limit(1);
  if (error) return null;
  return data?.[0] || null;
}